import { supabase } from '../supabase';

// Types
export interface CollaboratorPortalAccess {
  id: string;
  organization_id: string;
  external_partner_id: string;
  username: string;
  is_active: boolean;
  permissions: CollaboratorPortalPermissions;
  preferred_language: string;
  last_login_at?: string;
  login_attempts: number;
  locked_until?: string;
  created_at: string;
  partner?: {
    id: string;
    name: string;
    email?: string;
    company?: string;
    organization_id?: string;
  };
  projects?: CollaboratorPortalProject[];
}

export interface CollaboratorPortalProject {
  id: string;
  collaborator_access_id: string;
  project_id: string;
  project: {
    id: string;
    name: string;
    status: string;
    description?: string;
  };
}

export interface TaskTranslation {
  id: string;
  task_id: string;
  language: string;
  title: string;
  description?: string;
  checklist?: { id: string; title: string }[];
  translated_at: string;
  source_language: string;
}

export interface CommentTranslation {
  id: string;
  comment_id: string;
  comment_type: 'task_comment' | 'client_comment';
  language: string;
  content: string;
  translated_at: string;
}

export interface CollaboratorComment {
  id: string;
  content: string;
  created_at: string;
  author_type: 'team' | 'client' | 'collaborator';
  author_name: string;
  translation?: string;
}

export interface CollaboratorPortalSession {
  id: string;
  collaborator_access_id: string;
  session_token: string;
  expires_at: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface CollaboratorPortalPermissions {
  view_assigned_tasks: boolean;
  update_task_status: boolean;
  add_comments: boolean;
  view_public_comments: boolean;
  upload_attachments: boolean;
  log_time: boolean;
}

export interface CollaboratorAuthCredentials {
  username: string;
  password: string;
}

export interface CollaboratorAuthResponse {
  success: boolean;
  error?: string;
  access?: CollaboratorPortalAccess;
  session?: CollaboratorPortalSession;
}

export interface CollaboratorTask {
  id: string;
  project_id: string;
  title: string;
  description?: string;
  status: string;
  priority?: string;
  due_date?: string;
  completed_at?: string;
  created_at: string;
  section?: string;
  assignment_id: string;
  assigned_at: string;
  project?: {
    id: string;
    name: string;
  };
  checklist?: {
    id: string;
    title: string;
    is_completed: boolean;
  }[];
  tags?: {
    id: string;
    name: string;
    color: string;
  }[];
  attachments?: {
    id: string;
    name: string;
    url: string;
    type: string;
    size: number;
    created_at: string;
  }[];
  is_header?: boolean;
  order?: number;
}

interface CreateCollaboratorAccessData {
  external_partner_id: string;
  username: string;
  password: string;
  permissions?: Partial<CollaboratorPortalPermissions>;
  is_active?: boolean;
  preferred_language?: string;
  project_ids?: string[];
}

// Colonne leggibili dal browser. `password_hash` è escluso di proposito: la verifica avviene
// lato server con `portal_collaborator_login`, e dalla fase 3 dei fix di sicurezza il ruolo
// anon non ha più il permesso di leggerla, quindi `select('*')` fallirebbe con 42501.
// Vedi MD/security/SECURITY_AUDIT_2026-07-30_RLS.md
const ACCESS_COLUMNS = [
  'id', 'organization_id', 'external_partner_id', 'username', 'is_active', 'permissions',
  'last_login_at', 'login_attempts', 'locked_until', 'created_at', 'updated_at',
  'created_by', 'preferred_language'
].join(', ');

// Segnaposto usato alla creazione, prima che `portal_set_password` imposti quella vera.
// Non corrisponde a nessun formato valido, quindi non consente l'accesso.
const PASSWORD_PLACEHOLDER = '!non-impostata!';

// Durata sessione, tentativi massimi e durata del blocco vivono ora dentro
// `portal_collaborator_login`.

const DEFAULT_PERMISSIONS: CollaboratorPortalPermissions = {
  view_assigned_tasks: true,
  update_task_status: true,
  add_comments: true,
  view_public_comments: true,
  upload_attachments: true,
  log_time: false
};

export class CollaboratorAuthService {
  /**
   * Create new collaborator portal access
   */
  static async createCollaboratorAccess(data: CreateCollaboratorAccessData): Promise<CollaboratorPortalAccess | null> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) throw new Error('User profile not found');

      const permissions = { ...DEFAULT_PERMISSIONS, ...data.permissions };

      // La password non viene mai cifrata nel browser: riga creata con un segnaposto (la
      // colonna è NOT NULL) e password vera impostata subito dopo con `portal_set_password`.
      const { data: accessData, error } = await supabase
        .from('collaborator_portal_access')
        .insert({
          organization_id: userProfile.organization_id,
          external_partner_id: data.external_partner_id,
          username: data.username,
          password_hash: PASSWORD_PLACEHOLDER,
          permissions,
          preferred_language: data.preferred_language || 'it',
          is_active: data.is_active !== false,
          created_by: userData.user.id
        })
        .select(`
          ${ACCESS_COLUMNS},
          partner:external_partners(
            id, name, email, company
          )
        `)
        .single();

      if (error) throw error;

      const { error: passwordError } = await supabase.rpc('portal_set_password', {
        p_portal: 'collaborator',
        p_access_id: accessData.id,
        p_password: data.password
      });

      if (passwordError) {
        await supabase.from('collaborator_portal_access').delete().eq('id', accessData.id);
        throw passwordError;
      }

      // Insert project associations if provided
      if (data.project_ids && data.project_ids.length > 0) {
        const projectRows = data.project_ids.map(project_id => ({
          collaborator_access_id: accessData.id,
          project_id
        }));
        await supabase.from('collaborator_portal_projects').insert(projectRows);
      }

      // accessData non contiene password_hash: non è fra le colonne selezionate
      return accessData;
    } catch (error) {
      console.error('Error creating collaborator access:', error);
      return null;
    }
  }

  /**
   * Authenticate collaborator and create session
   */
  static async authenticateCollaborator(
    credentials: CollaboratorAuthCredentials,
    ipAddress?: string,
    userAgent?: string
  ): Promise<CollaboratorAuthResponse> {
    try {
      // Autenticazione interamente lato server: blocco account, verifica password,
      // tentativi, creazione sessione. Restituisce l'accesso privo di `password_hash`.
      const { data, error } = await supabase.rpc('portal_collaborator_login', {
        p_username: credentials.username,
        p_password: credentials.password,
        p_ip: ipAddress ?? null,
        p_user_agent: userAgent ?? null
      });

      if (error) throw error;
      if (!data?.success) {
        return { success: false, error: data?.error || 'Credenziali non valide' };
      }

      const access = data.access;

      // Normalize permissions
      if (typeof access.permissions === 'string') {
        try {
          access.permissions = JSON.parse(access.permissions);
        } catch {
          access.permissions = DEFAULT_PERMISSIONS;
        }
      }

      return {
        success: true,
        access,
        session: data.session
      };
    } catch (error) {
      console.error('Error authenticating collaborator:', error);
      return { success: false, error: 'Autenticazione fallita' };
    }
  }

  /**
   * Validate collaborator session
   */
  static async validateSession(sessionToken: string): Promise<CollaboratorPortalAccess | null> {
    try {
      // Validazione lato server: il browser non legge più la tabella delle sessioni e non
      // può quindi enumerare i token altrui.
      const { data, error } = await supabase.rpc('portal_collaborator_validate_session', {
        p_token: sessionToken
      });

      if (error || !data) {
        return null;
      }

      // Normalize permissions
      if (typeof data.permissions === 'string') {
        try {
          data.permissions = JSON.parse(data.permissions);
        } catch {
          data.permissions = DEFAULT_PERMISSIONS;
        }
      }

      return data;
    } catch (error) {
      console.error('Error validating session:', error);
      return null;
    }
  }

  /**
   * Logout collaborator
   */
  static async logoutCollaborator(sessionToken: string): Promise<boolean> {
    try {
      const { error } = await supabase.rpc('portal_collaborator_logout', { p_token: sessionToken });
      return !error;
    } catch (error) {
      console.error('Error logging out collaborator:', error);
      return false;
    }
  }

  // Le letture e le scritture fatte DAL portale collaboratore (task assegnati, commenti,
  // cambio stato, traduzioni) non stanno più qui: passano dalle RPC di sessione in
  // src/lib/services/portal/collaboratorPortalApi.ts. Qui resta ciò che gira con il JWT di
  // uno staff loggato nel CRM, più le edge function di traduzione condivise fra i due lati.

  /**
   * Assign task to collaborator (CRM function)
   */
  static async assignTaskToCollaborator(
    taskId: string,
    collaboratorAccessId: string
  ): Promise<boolean> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return false;

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) return false;

      // Get partner_id, language, and task's project_id
      const { data: accessData } = await supabase
        .from('collaborator_portal_access')
        .select('external_partner_id, preferred_language')
        .eq('id', collaboratorAccessId)
        .single();

      if (!accessData) return false;

      // Get the task's project_id
      const { data: taskData } = await supabase
        .from('tasks')
        .select('project_id')
        .eq('id', taskId)
        .single();

      const { error } = await supabase
        .from('collaborator_task_assignments')
        .upsert({
          organization_id: userProfile.organization_id,
          task_id: taskId,
          collaborator_access_id: collaboratorAccessId,
          external_partner_id: accessData.external_partner_id,
          assigned_by: userData.user.id
        }, { onConflict: 'task_id,collaborator_access_id' });

      if (error) {
        console.error('Error inserting collaborator_task_assignments:', error);
        return false;
      }

      // Set assigned_collaborator_access_id on the task (like assigned_client_access_id for clients)
      await supabase
        .from('tasks')
        .update({ assigned_collaborator_access_id: collaboratorAccessId })
        .eq('id', taskId);

      // Auto-associate collaborator to the project if not already
      if (taskData?.project_id) {
        const { data: existingLink } = await supabase
          .from('collaborator_portal_projects')
          .select('id')
          .eq('collaborator_access_id', collaboratorAccessId)
          .eq('project_id', taskData.project_id)
          .maybeSingle();

        if (!existingLink) {
          await supabase
            .from('collaborator_portal_projects')
            .insert({
              collaborator_access_id: collaboratorAccessId,
              project_id: taskData.project_id
            });
        }
      }

      // Trigger translation if collaborator has a different language
      if (accessData.preferred_language && accessData.preferred_language !== 'it') {
        this.triggerTaskTranslation(taskId, accessData.preferred_language).catch(() => {});
      }

      return true;
    } catch (error) {
      console.error('Error assigning task:', error);
      return false;
    }
  }

  /**
   * Remove task assignment
   */
  static async removeTaskAssignment(taskId: string, collaboratorAccessId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('collaborator_task_assignments')
        .delete()
        .eq('task_id', taskId)
        .eq('collaborator_access_id', collaboratorAccessId);

      return !error;
    } catch (error) {
      console.error('Error removing assignment:', error);
      return false;
    }
  }

  /**
   * Get all collaborator accesses for organization
   */
  static async getCollaboratorAccessList(): Promise<CollaboratorPortalAccess[]> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return [];

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) return [];

      const { data, error } = await supabase
        .from('collaborator_portal_access')
        .select(`
          ${ACCESS_COLUMNS},
          partner:external_partners(
            id, name, email, company
          )
        `)
        .eq('organization_id', userProfile.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // password_hash non è fra le colonne selezionate
      return data || [];
    } catch (error) {
      console.error('Error fetching collaborator access list:', error);
      return [];
    }
  }

  /**
   * Reset collaborator password
   */
  static async resetCollaboratorPassword(accessId: string, newPassword: string): Promise<boolean> {
    try {
      // L'hash lo calcola il database (bcrypt). La funzione azzera i tentativi, toglie il
      // blocco e cancella le sessioni aperte.
      const { error } = await supabase.rpc('portal_set_password', {
        p_portal: 'collaborator',
        p_access_id: accessId,
        p_password: newPassword
      });

      return !error;
    } catch (error) {
      console.error('Error resetting password:', error);
      return false;
    }
  }

  /**
   * Disable collaborator access
   */
  static async disableCollaboratorAccess(accessId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('collaborator_portal_access')
        .update({ is_active: false })
        .eq('id', accessId);

      if (error) return false;

      // Invalidate all sessions
      await supabase
        .from('collaborator_portal_sessions')
        .delete()
        .eq('collaborator_access_id', accessId);

      return true;
    } catch (error) {
      console.error('Error disabling access:', error);
      return false;
    }
  }

  /**
   * Enable collaborator access
   */
  static async enableCollaboratorAccess(accessId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('collaborator_portal_access')
        .update({ is_active: true })
        .eq('id', accessId);

      return !error;
    } catch (error) {
      console.error('Error enabling access:', error);
      return false;
    }
  }

  // ========================================
  // Projects Management
  // ========================================

  /**
   * Get projects accessible by a collaborator
   */
  static async getCollaboratorProjects(accessId: string): Promise<CollaboratorPortalProject[]> {
    try {
      const { data, error } = await supabase
        .from('collaborator_portal_projects')
        .select(`
          id,
          collaborator_access_id,
          project_id,
          project:projects(id, name, status, description)
        `)
        .eq('collaborator_access_id', accessId);

      if (error) throw error;
      return (data || []) as CollaboratorPortalProject[];
    } catch (error) {
      console.error('Error fetching collaborator projects:', error);
      return [];
    }
  }

  /**
   * Update collaborator's project associations
   */
  static async updateCollaboratorProjects(accessId: string, projectIds: string[]): Promise<boolean> {
    try {
      // Delete existing associations
      await supabase
        .from('collaborator_portal_projects')
        .delete()
        .eq('collaborator_access_id', accessId);

      // Insert new ones
      if (projectIds.length > 0) {
        const rows = projectIds.map(project_id => ({
          collaborator_access_id: accessId,
          project_id
        }));

        const { error } = await supabase
          .from('collaborator_portal_projects')
          .insert(rows);

        if (error) throw error;
      }

      // Cleanup orphaned task assignments (tasks from removed projects)
      const { data: assignments } = await supabase
        .from('collaborator_task_assignments')
        .select(`
          id,
          task:tasks(project_id)
        `)
        .eq('collaborator_access_id', accessId);

      if (assignments) {
        const orphanIds = assignments
          .filter(a => a.task && !projectIds.includes((a.task as any).project_id))
          .map(a => a.id);

        if (orphanIds.length > 0) {
          await supabase
            .from('collaborator_task_assignments')
            .delete()
            .in('id', orphanIds);
        }
      }

      return true;
    } catch (error) {
      console.error('Error updating collaborator projects:', error);
      return false;
    }
  }

  /**
   * Update collaborator access (permissions, language, projects)
   */
  static async updateCollaboratorAccess(
    accessId: string,
    data: {
      permissions?: Partial<CollaboratorPortalPermissions>;
      preferred_language?: string;
      project_ids?: string[];
    }
  ): Promise<boolean> {
    try {
      // Update access record
      const updateData: Record<string, unknown> = {};
      if (data.permissions) updateData.permissions = { ...DEFAULT_PERMISSIONS, ...data.permissions };
      if (data.preferred_language) updateData.preferred_language = data.preferred_language;

      if (Object.keys(updateData).length > 0) {
        const { error } = await supabase
          .from('collaborator_portal_access')
          .update(updateData)
          .eq('id', accessId);
        if (error) throw error;
      }

      // Update projects if provided
      if (data.project_ids !== undefined) {
        await this.updateCollaboratorProjects(accessId, data.project_ids);
      }

      return true;
    } catch (error) {
      console.error('Error updating collaborator access:', error);
      return false;
    }
  }

  // ========================================
  // Translations (edge function, usate sia dal CRM sia dal portale)
  // ========================================

  /**
   * Trigger translation for a task via edge function
   */
  static async triggerTaskTranslation(taskId: string, targetLanguage: string): Promise<TaskTranslation | null> {
    if (targetLanguage === 'it') return null;

    try {
      const { data, error } = await supabase.functions.invoke('translate-task', {
        body: { task_id: taskId, target_language: targetLanguage }
      });
      if (error) {
        // Log full error context for debugging
        const ctx = (error as any)?.context;
        if (ctx?.json) {
          const body = await ctx.json().catch(() => null);
          console.error('Error triggering task translation:', error, 'Response body:', body);
        } else {
          console.error('Error triggering task translation:', error);
        }
        return null;
      }
      return data?.translation || null;
    } catch (error) {
      console.error('Error triggering task translation:', error);
      return null;
    }
  }

  /**
   * Trigger translation for a comment via edge function
   */
  static async triggerCommentTranslation(
    commentId: string,
    commentType: 'task_comment' | 'client_comment',
    targetLanguage: string
  ): Promise<void> {
    if (targetLanguage === 'it') return;

    try {
      await supabase.functions.invoke('translate-comment', {
        body: { comment_id: commentId, comment_type: commentType, target_language: targetLanguage }
      });
    } catch (error) {
      console.error('Error triggering comment translation:', error);
    }
  }

  // ========================================
  // Project Collaborators (CRM-side queries)
  // ========================================

  /**
   * Get all collaborator accesses that have access to a specific project
   */
  static async getProjectCollaborators(projectId: string): Promise<(CollaboratorPortalAccess & { task_count: number })[]> {
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) return [];

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile) return [];

      // Get collaborator accesses linked to this project
      const { data: projectAccesses } = await supabase
        .from('collaborator_portal_projects')
        .select(`
          collaborator_access_id,
          collaborator_access:collaborator_portal_access(
            ${ACCESS_COLUMNS},
            partner:external_partners(id, name, email, company)
          )
        `)
        .eq('project_id', projectId);

      if (!projectAccesses) return [];

      // Get task counts for each collaborator
      const result: (CollaboratorPortalAccess & { task_count: number })[] = [];

      for (const pa of projectAccesses) {
        const access = pa.collaborator_access as any;
        if (!access) continue;

        const { count } = await supabase
          .from('collaborator_task_assignments')
          .select('id', { count: 'exact', head: true })
          .eq('collaborator_access_id', pa.collaborator_access_id)
          .in('task_id',
            supabase.from('tasks').select('id').eq('project_id', projectId) as any
          );

        // Simpler approach: count all assignments for this collaborator
        const { data: taskAssignments } = await supabase
          .from('collaborator_task_assignments')
          .select(`
            id,
            task:tasks(project_id)
          `)
          .eq('collaborator_access_id', pa.collaborator_access_id);

        const taskCount = (taskAssignments || []).filter(
          a => a.task && (a.task as any).project_id === projectId
        ).length;

        // access non contiene password_hash: non è fra le colonne selezionate
        result.push({ ...access, task_count: taskCount });
      }

      return result;
    } catch (error) {
      console.error('Error fetching project collaborators:', error);
      return [];
    }
  }

  /**
   * Add a collaborator access to a project
   */
  static async addCollaboratorToProject(collaboratorAccessId: string, projectId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('collaborator_portal_projects')
        .insert({
          collaborator_access_id: collaboratorAccessId,
          project_id: projectId
        });

      return !error;
    } catch (error) {
      console.error('Error adding collaborator to project:', error);
      return false;
    }
  }

  /**
   * Remove a collaborator access from a project
   */
  static async removeCollaboratorFromProject(collaboratorAccessId: string, projectId: string): Promise<boolean> {
    try {
      // Remove project association
      await supabase
        .from('collaborator_portal_projects')
        .delete()
        .eq('collaborator_access_id', collaboratorAccessId)
        .eq('project_id', projectId);

      // Cleanup task assignments for this project
      const { data: assignments } = await supabase
        .from('collaborator_task_assignments')
        .select(`id, task:tasks(project_id)`)
        .eq('collaborator_access_id', collaboratorAccessId);

      if (assignments) {
        const orphanIds = assignments
          .filter(a => a.task && (a.task as any).project_id === projectId)
          .map(a => a.id);

        if (orphanIds.length > 0) {
          await supabase
            .from('collaborator_task_assignments')
            .delete()
            .in('id', orphanIds);
        }
      }

      return true;
    } catch (error) {
      console.error('Error removing collaborator from project:', error);
      return false;
    }
  }

  // ==================== Project Translations ====================

  /**
   * Trigger project name translation via edge function
   */
  static async triggerProjectTranslation(projectId: string, targetLanguage: string): Promise<void> {
    if (targetLanguage === 'it') return;

    try {
      await supabase.functions.invoke('translate-project', {
        body: { project_id: projectId, target_language: targetLanguage }
      });
    } catch (error) {
      console.error('Error triggering project translation:', error);
    }
  }
}
